import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, MenuIcon, ShoppingCartIcon, StarIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { livingNav, livingUsps } from '../../data/navigation';
import { Logo } from '../brand/Logo';
import { SearchBox } from '../layout/SearchBox';
import { MobileMenu } from '../layout/MobileMenu';
const ease = [0.23, 1, 0.32, 1] as const;
export function LivingHeader() {
  const {
    count,
    open
  } = useCart();
  const reduce = useReducedMotion();
  const [menuOpen, setMenuOpen] = useState(false);
  const [usp, setUsp] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setUsp(u => (u + 1) % livingUsps.length), 4000);
    return () => window.clearInterval(t);
  }, []);
  const navItems = livingNav.filter(n => n.to !== '/living/collectie/alles');
  const linkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `whitespace-nowrap text-[15px] font-semibold transition-colors duration-150 ${isActive ? 'text-majorelle' : 'text-ink hover:text-majorelle'}`;
  return <>
      <header className="sticky top-0 z-40 bg-white" data-id="element-329" data-mp="22uw97c2ayg8d">
        <div className="bg-majorelle text-white" data-id="element-330" data-mp="22vfnmisqumhn">
          <div className="mx-auto grid h-9 max-w-7xl grid-cols-1 items-center px-5 text-xs sm:grid-cols-[1fr_auto_1fr] lg:px-8" data-id="element-331" data-mp="27tpkcxu49v5d">
            <div className="hidden items-center gap-5 sm:flex" data-id="element-332" data-mp="1b4rm35n5rf3g">
              <Link to="/" className="uppercase tracking-[0.16em] text-white/60 transition-colors duration-150 hover:text-white" data-id="element-333" data-mp="1f51sgjev47iu">
                Beauty
              </Link>
              <span className="font-semibold uppercase tracking-[0.16em]" data-id="element-334" data-mp="1k6hd4ad4oc0p">Living</span>
            </div>
            <div className="relative h-9 overflow-hidden text-center" aria-live="polite" data-id="element-335" data-mp="1p14w5tpd4e7w">
              <AnimatePresence mode="wait" initial={false} data-id="element-336" data-mp="2vlnx7m97rykm">
                <motion.p key={usp} initial={reduce ? false : {
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={reduce ? undefined : {
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.2,
                ease
              }} className="flex h-9 items-center justify-center gap-1.5 whitespace-nowrap font-medium" data-id="element-337" data-mp="30hlbln0x2uur">
                  <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" data-id="element-338" data-mp="2fu50z93r716r" />
                  {livingUsps[usp]}
                </motion.p>
              </AnimatePresence>
            </div>
            <p className="hidden items-center justify-end gap-1.5 md:flex" data-id="element-339" data-mp="1n6hiq0pygmna">
              <StarIcon className="h-3 w-3 fill-white" strokeWidth={0} aria-hidden="true" data-id="element-340" data-mp="21uvw35kzzti3" />
              4,8 · 3.100+ reviews
            </p>
          </div>
        </div>

        <div className="border-b border-line" data-id="element-341" data-mp="19d9qq7xypp52">
          <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-5 lg:h-20 lg:gap-10 lg:px-8" data-id="element-342" data-mp="19byquj7n11j8">
            <button type="button" onClick={() => setMenuOpen(true)} className="-ml-2 grid h-10 w-10 place-items-center lg:hidden" aria-label="Menu openen" data-id="element-343" data-mp="14e6oev9d9x0s">
              <MenuIcon className="h-5 w-5" data-id="element-344" data-mp="3kss6urs48mbz" />
            </button>
            <Link to="/living" aria-label="AMIRA Living, naar home" className="shrink-0" data-id="element-345" data-mp="363madbknxa7s">
              <Logo sub="LIVING" data-id="element-346" data-mp="361rn3qlx560j" />
            </Link>
            <div className="mx-auto hidden w-full max-w-2xl md:block" data-id="element-347" data-mp="3b3qs14xb4azl">
              <SearchBox line="living" data-id="element-348" data-mp="2n5hvksxvbuw" />
            </div>
            <button type="button" onClick={open} className="ml-auto flex h-11 shrink-0 items-center gap-2 rounded-full bg-terracotta pl-4 pr-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-terracotta-dark md:ml-0" aria-label={`Winkelwagen, ${count} artikelen`} data-id="element-349" data-mp="2np1d5y8m8b1">
              <ShoppingCartIcon className="h-4 w-4" aria-hidden="true" data-id="element-350" data-mp="2ec6ndgf71lh1" />
              <span className="hidden sm:inline" data-id="element-351" data-mp="2idrzgzjwlvq6">Winkelwagen</span>
              <span className="grid h-7 min-w-7 place-items-center rounded-full bg-white/20 px-1.5 text-xs tabular-nums" data-id="element-352" data-mp="2ibxc7el5trix">{count}</span>
            </button>
          </div>
          <div className="px-5 pb-3 md:hidden" data-id="element-353" data-mp="2tto6p35t3gm8">
            <SearchBox line="living" data-id="element-354" data-mp="2ovcwusfih640" />
          </div>
        </div>

        <nav aria-label="Categorieën" className="hidden border-b border-line lg:block" data-id="element-355" data-mp="2ovmoslk49ncc">
          <ul className="mx-auto flex h-12 max-w-7xl items-center justify-center gap-9 px-8" data-id="element-356" data-mp="2jxuy1uv7k79q">
            {navItems.map(item => <li key={item.to} data-id="element-357" data-mp="3hho7yx0qupf2">
                <NavLink to={item.to} className={linkClass} data-id="element-358" data-mp="3cko1wm3w9mny">{item.label}</NavLink>
              </li>)}
            <li data-id="element-359" data-mp="37ktdqqbi7b6l">
              <Link to="/" className="whitespace-nowrap text-[15px] font-semibold text-pink transition-colors duration-150 hover:text-berry" data-id="element-360" data-mp="37k9th2ydsape">
                AMIRA Beauty
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} line="living" data-id="element-361" data-mp="1s5cb72afv5qx" />
    </>;
}